import type {
  Competition,
  Direction,
  LessonTypeRef,
  News,
  RegionRef,
  School,
  Trainer,
} from '~/api'
import { REGION_ID } from '~/stores/common'
import type { SearchParams } from '~/types'

export const useDirectoryStore = defineStore(
  'directory',
  () => {
    const api = useApi()
    const regions = ref<RegionRef[]>([])

    const directions = ref<Partial<Direction>[]>([
      { name: 'Футбол' },
      { name: 'Баскетбол' },
      { name: 'Плавание' },
      { name: 'Волейбол' },
      { name: 'Гимнастика' },
      { name: 'Шахматы' },
      { name: 'Хоккей' },
    ])
    const lessonTypes = ref<LessonTypeRef[]>([])
    const schools = ref<School[]>([])
    const trainers = ref<Trainer[]>([])
    const competitions = ref<Competition[]>([])
    const news = ref<News[]>([])

    interface ICity {
      label: string
      id: string
    }

    const cities = ref<ICity[]>([
      { label: 'Москва', id: REGION_ID.MSK },
      { label: 'Санкт-Петербург', id: REGION_ID.MSK },
      { label: 'Екатеринбург', id: REGION_ID.MSK },
      { label: 'Казань', id: REGION_ID.MSK },
      { label: 'Краснодар', id: REGION_ID.MSK },
      { label: 'Нижний Новгород', id: REGION_ID.MSK },
      { label: 'Новосибирск', id: REGION_ID.MSK },
      { label: 'Омск', id: REGION_ID.MSK },
      { label: 'Ростов-на-Дону', id: REGION_ID.MSK },
      { label: 'Самара', id: REGION_ID.MSK },
      { label: 'Челябинск', id: REGION_ID.MSK },
    ])

    const selectedRegion = ref<RegionRef | undefined>(undefined)
    const selectedRegionLabel = computed(() => selectedRegion.value?.name)

    const regionId = computed(() => selectedRegion.value?.id || REGION_ID.MSK)

    const selectRegionById = (id: string) => {
      selectedRegion.value = regions.value.find((region) => region.id === id)
    }

    const fetchRegions = async (q?: string) => {
      try {
        const { data } = await api.public.regions({ q })
        regions.value = data
      } catch (error) {
        console.warn(
          'Не удалось загрузить регионы, используется fallback:',
          error,
        )
        regions.value = [
          { id: REGION_ID.MSK, name: 'Москва', full_name: 'Москва' },
        ]
      }
    }

    const fetchSchools = async (params?: Omit<SearchParams, 'region_id'>) => {
      if (!regionId.value) return
      const { data } = await api.public.schools({
        region_id: regionId.value,
        ...(params || {}),
      })
      schools.value = data.items
      return data.items
    }

    const fetchDirections = async (
      params?: Omit<SearchParams | any, 'region_id'>,
    ) => {
      if (directions.value.some((d) => d.id) && !params) return
      const { data } = await api.public.directions({
        ...(params || {}),
      })
      directions.value = data
      return data
    }

    const fetchLessonTypes = async () => {
      if (lessonTypes.value.length > 0) return
      const { data } = await api.public.lessonTypes()
      lessonTypes.value = data
    }

    const fetchTrainers = async (params?: Omit<SearchParams, 'region_id'>) => {
      if (!regionId.value) return
      const { data } = await api.public.trainers({
        region_id: regionId.value,
        ...(params || {}),
      })
      trainers.value = data.items
    }

    const fetchCompetitions = async () => {
      if (!regionId.value) return
      const { data } = await api.public.competitions({
        region_id: regionId.value,
      })
      competitions.value = data.items
    }

    const fetchNews = async (
      params?: Omit<
        SearchParams & {
          school_id?: string | null
          trainer_id?: string | null
        },
        'region_id'
      >,
    ) => {
      if (!regionId.value) return
      const { data } = await api.public.news({
        region_id: regionId.value,
        ...(params || {}),
      })
      news.value = data.items
      return data.items
    }

    watch(selectedRegion, (newValue, oldValue) => {
      if (import.meta.client) {
        if (
          newValue?.name &&
          oldValue?.name &&
          oldValue.name !== newValue.name
        ) {
          // window.location.reload()
        }
      }
    })

    nextTick(() => {
      if (!selectedRegion.value && regions.value) {
        selectedRegion.value = regions.value.find(
          (region) => region.id === REGION_ID.MSK,
        )
      }
    })

    return {
      regions,
      selectedRegion,
      selectedRegionLabel,
      cities,
      regionId,
      directions,
      lessonTypes,
      schools,
      trainers,
      competitions,
      news,

      fetchTrainers,
      fetchCompetitions,
      fetchDirections,
      fetchLessonTypes,
      fetchNews,
      fetchRegions,
      fetchSchools,
      selectRegionById,
    }
  },
  {
    persist: {
      // Cookie доступен на SSR — иначе localStorage даёт MSK на сервере и другой регион на клиенте
      storage: piniaPluginPersistedstate.cookies({
        maxAge: 60 * 60 * 24 * 365,
        sameSite: 'lax',
      }),
      pick: ['selectedRegion'],
    },
  },
)
